import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
} from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { catchErrorMessage } from 'utility/helpers';
import useUserData, { UserType } from 'hooks/useUserData';
import UserAPI from 'api/UserAPI';
import CustomDialogTitle from 'components/modal/CustomDialogTitle';
import GrowDialogTransition from 'components/modal/GrowDialogTransition';
import { Dispatch, SetStateAction, useMemo, useRef, useState } from 'react';
import { updateUserDetailsCache } from '@buildwise/libs/people/queries/useUserProfileData';
import { ReactComponent as SMEBadge } from 'assets/icons/sme-badge.svg';
import _ from 'lodash';
import ProfileSkillsForm from './ProfileSkillsForm';
import { SkillCategoryType } from '@buildwise/libs/people/components/Details/Skills';
import { ConfigAPI, getConfigEntriesList, useConfigQuery } from '@buildwise/libs/configurations';
import { useBuildWiseModules } from '@buildwise/libs/helpers';

export type SkillsAndHobbiesFormData = {
  mySkills: string[];
  hobbies: string[];
  SMESkills: string[];
  projectTypes: string[];
  buildingSystems: string[];
  professionalExperiences: string[];
};

type SkillsAndHobbiesModalProps = {
  canDoItLater?: boolean;
  type?: SkillCategoryType;
  open: boolean;
  shouldUpdateSkillsAndHobbies?: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  isLoggedInUser: boolean;
  userDetails: UserType;
};

const configList = {
  skills: 'userProfileSuggestedExpertSkills',
  hobbies: 'HOBBIES',
  smeSkills: 'SME_TOPICS',
  projectTypes: 'PROJECT_TYPES',
  buildingSystems: 'BUILDING_SYSTEMS',
  professionalExperiences: 'PROFESSIONAL_EXPERIENCE',
};

const modalTypeMappingWithFormData = {
  skills: 'mySkills',
  hobbies: 'hobbies',
  smeSkills: 'SMESkills',
  projectTypes: 'projectTypes',
  buildingSystems: 'buildingSystems',
  professionalExperiences: 'professionalExperiences',
};

function SkillsAndHobbiesModal({
  canDoItLater = false,
  type = 'skills',
  open,
  shouldUpdateSkillsAndHobbies = false,
  setOpen,
  userDetails,
}: SkillsAndHobbiesModalProps) {
  const { companyId } = useUserData();
  const [formType, setFormType] = useState(type);
  const { singularProjectsModuleName } = useBuildWiseModules();
  const skillsContainerRef = useRef<HTMLDivElement | null>(null);
  const smeSkillsContainerRef = useRef<HTMLDivElement | null>(null);
  const hobbiesContainerRef = useRef<HTMLDivElement | null>(null);
  const projectTypeContainerRef = useRef<HTMLDivElement | null>(null);
  const buildingSystemsContainerRef = useRef<HTMLDivElement | null>(null);
  const professionalExperienceContainerRef = useRef<HTMLDivElement | null>(null);
  const {
    activeUniqueEntries,
    isLoading: isConfigLoading,
    config,
    updateConfigCache,
  } = useConfigQuery({
    companyId,
    configName: configList[type],
  });

  const entriesList = useMemo(
    () => getConfigEntriesList({ data: activeUniqueEntries }),
    [activeUniqueEntries],
  );

  const formData = useForm<SkillsAndHobbiesFormData>({
    mode: 'all',
    defaultValues: {
      mySkills: userDetails?.mySkills || [],
      hobbies: userDetails?.hobbies || [],
      SMESkills: userDetails?.SMESkills || [],
      projectTypes: userDetails?.projectTypes || [],
      buildingSystems: userDetails?.buildingSystems || [],
      professionalExperiences: userDetails?.professionalExperiences || [],
    },
  });
  const {
    formState: { isDirty, isSubmitting, isValid },
    handleSubmit,
    reset,
  } = formData;

  const updateConfig = async (newEntries: string[], prevConfig) => {
    const { data } = await ConfigAPI.updateConfig({
      ...prevConfig,
      companyId,
      entries: [
        ...prevConfig?.entries,
        ...newEntries?.map((topic) => ({
          entryCode: topic,
          entryDescription: topic,
          isActive: true,
          entryValues: [],
        })),
      ],
    });
    updateConfigCache({
      companyId,
      configName: configList[type],
      updatedConfig: {
        config: data.config,
      },
    });
  };

  const onSubmit = async (values: SkillsAndHobbiesFormData) => {
    try {
      const payload = {
        ...userDetails,
        ...values,
      };
      const { data } = await UserAPI.editUser(payload);
      updateUserDetailsCache({ ...userDetails, ...data });

      const newValues = _.difference(values?.[modalTypeMappingWithFormData[type]], entriesList);

      // Updating the company config if new values are added
      if (newValues && newValues.length > 0) {
        updateConfig(newValues, config);
      }

      if (shouldUpdateSkillsAndHobbies && formType === 'skills') {
        reset(
          {
            ...values,
          },
          { keepDirty: false, keepIsValid: false },
        );
        setFormType('hobbies');
        if (skillsContainerRef.current) {
          skillsContainerRef.current.style.animationName = 'skills-slide';
        }
        if (hobbiesContainerRef.current) {
          hobbiesContainerRef.current.style.animationName = 'hobbies-slide';
        }

        setTimeout(() => {
          if (skillsContainerRef.current) {
            skillsContainerRef.current.style.display = 'none';
          }
        }, 300);
      }

      if (!shouldUpdateSkillsAndHobbies || formType === 'hobbies') {
        toast.success('Profile Updated Successfully');
        closeModal();
      }
    } catch (e) {
      toast.error(catchErrorMessage(e));
    }
  };

  const closeModal = () => {
    reset();
    setOpen(false);
  };

  const modalTitles = {
    skills: 'My Skills',
    smeSkills: 'Add SME Topics',
    hobbies: 'My Hobbies',
    projectTypes: `${singularProjectsModuleName} Types`,
    buildingSystems: 'Building Systems',
    professionalExperiences: 'Professional Experience',
  };

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="lg"
      aria-labelledby="dialog-title"
      aria-describedby="dialog-description"
      TransitionComponent={GrowDialogTransition}
      sx={{ '& .MuiPaper-root': { height: '80%' } }}
    >
      <CustomDialogTitle onClose={closeModal} id="dialog-title" sx={{ pb: 0.5 }}>
        <Box display="flex" alignItems="center">
          {formType === 'smeSkills' && (
            <SMEBadge style={{ width: 28, height: 28, marginRight: 6 }} />
          )}
          <Title>{modalTitles[formType]}</Title>
        </Box>
      </CustomDialogTitle>
      <DialogContent id="dialog-description" sx={{ py: 0, overflowX: 'hidden' }}>
        <FormProvider {...formData}>
          <SkillsFormContainer
            ref={skillsContainerRef}
            display={formType === 'skills' || shouldUpdateSkillsAndHobbies ? 'block' : 'none'}
          >
            {(formType === 'skills' || shouldUpdateSkillsAndHobbies) && (
              <ProfileSkillsForm
                suggestedSkills={entriesList}
                suggestionsLoading={isConfigLoading}
                fieldKey="mySkills"
                title="Add skills you're knowledgeable or interested in"
                labelName="Skills"
                placeholder="i.e. Construction, Project Planning, etc."
              />
            )}
          </SkillsFormContainer>
          <HobbieFormContainer
            ref={hobbiesContainerRef}
            display={formType === 'hobbies' ? 'block' : 'none'}
          >
            {formType === 'hobbies' && (
              <ProfileSkillsForm
                suggestedSkills={entriesList}
                suggestionsLoading={isConfigLoading}
                fieldKey="hobbies"
                title="Let others know what hobbies you are enthusiastic about."
                labelName="Hobbies"
                placeholder="i.e. Snowboarding, Chess, Cooking, etc."
              />
            )}
          </HobbieFormContainer>
          <SMESkillsFormContainer
            ref={smeSkillsContainerRef}
            display={formType === 'smeSkills' ? 'block' : 'none'}
          >
            {formType === 'smeSkills' && (
              <ProfileSkillsForm
                suggestedSkills={entriesList}
                suggestionsLoading={isConfigLoading}
                fieldKey="SMESkills"
                title={`Add topics that ${userDetails?.userNameToDisplay} is a Subject Matter Expert in.`}
                labelName="Topics"
                placeholder="i.e. Construction, Project Planning, etc."
              />
            )}
          </SMESkillsFormContainer>
          <ProjectTypesFormContainer
            ref={projectTypeContainerRef}
            display={formType === 'projectTypes' ? 'block' : 'none'}
          >
            {formType === 'projectTypes' && (
              <ProfileSkillsForm
                suggestedSkills={entriesList}
                suggestionsLoading={isConfigLoading}
                fieldKey="projectTypes"
                title={`Add ${singularProjectsModuleName.toLocaleLowerCase()} types you’re knowledgeable about.`}
                labelName={`${singularProjectsModuleName} Types`}
                placeholder="i.e. Commercial, Healthcare, etc."
              />
            )}
          </ProjectTypesFormContainer>
          <BuildingSystemsFormContainer
            ref={buildingSystemsContainerRef}
            display={formType === 'buildingSystems' ? 'block' : 'none'}
          >
            {formType === 'buildingSystems' && (
              <ProfileSkillsForm
                suggestedSkills={entriesList}
                suggestionsLoading={isConfigLoading}
                fieldKey="buildingSystems"
                title="Add building systems you’re knowledgeable about."
                labelName="Building Systems"
                placeholder="i.e. Airfields, Cooling Systems, etc."
              />
            )}
          </BuildingSystemsFormContainer>
          <ProfessionalExperienceFormContainer
            ref={professionalExperienceContainerRef}
            display={formType === 'professionalExperiences' ? 'block' : 'none'}
          >
            {formType === 'professionalExperiences' && (
              <ProfileSkillsForm
                suggestedSkills={entriesList}
                suggestionsLoading={isConfigLoading}
                fieldKey="professionalExperiences"
                title="Add your professional experience."
                labelName="Professional Experience"
                placeholder="i.e. Architect, Designer, etc."
              />
            )}
          </ProfessionalExperienceFormContainer>
        </FormProvider>
      </DialogContent>
      <DialogActions>
        <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
          <Box>
            {canDoItLater && (
              <Button
                sx={(theme) => ({ color: theme.palette.custom.placeholder })}
                onClick={closeModal}
                disabled={isSubmitting}
              >
                I’ll do this later
              </Button>
            )}
          </Box>
          <Button
            disabled={isSubmitting || !isDirty || !isValid}
            onClick={handleSubmit(onSubmit)}
            variant="contained"
          >
            {isSubmitting ? (
              <CircularProgress sx={(theme) => ({ color: theme.palette.custom.black })} size={20} />
            ) : !shouldUpdateSkillsAndHobbies || formType === 'hobbies' ? (
              'Save'
            ) : (
              'Next'
            )}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

const Title = styled(Typography, { label: 'Title' })(({ theme }) => ({
  fontSize: 24,
  fontWeight: 700,
  color: theme.palette.custom.black,
}));

const HobbieFormContainer = styled(Box, { label: 'HobbieFormContainer' })({
  animationDuration: '0.6s',
  animationFillMode: 'both',
  '@keyframes hobbies-slide': {
    '0%': {
      transform: 'translateX(80%)',
      opacity: 0,
    },
    '100%': {
      transform: 'translateX(0%)',
      opacity: 1,
    },
  },
});

const createSlideAnimation = (animationName: string) => ({
  animationDuration: '0.6s',
  animationFillMode: 'both',
  [`@keyframes ${animationName}`]: {
    '0%': {
      transform: 'translateX(-20%)',
      opacity: 1,
    },
    '100%': {
      transform: 'translateX(-100%)',
      opacity: 0,
    },
  },
});
const SkillsFormContainer = styled(Box, { label: 'SkillsFormContainer' })(
  createSlideAnimation('skills-slide'),
);

const SMESkillsFormContainer = styled(Box, { label: 'SMESkillsFormContainer' })(
  createSlideAnimation('smeSkills-slide'),
);

const ProjectTypesFormContainer = styled(Box, { label: 'ProjectTypesFormContainer' })(
  createSlideAnimation('projectTypes-slide'),
);

const BuildingSystemsFormContainer = styled(Box, {
  label: 'BuildingSystemsFormContainer',
})(createSlideAnimation('buildingSystems-slide'));

const ProfessionalExperienceFormContainer = styled(Box, {
  label: 'ProfessionalExperienceFormContainer',
})(createSlideAnimation('professionalExperience-slide'));

export default SkillsAndHobbiesModal;
